import { CATALOG } from '@center-platform/component-catalog';
import type { SpaceDocument } from 'types/space';
import { hasAdapter } from '../composition-host/adapterRegistry';
import type { ContextViewBinding } from './composerStore';

const SPACE_CATALOG_TYPES = new Set(
  CATALOG.filter((entry) => entry.surfaces.includes('space')).map((entry) => entry.type)
);

/**
 * Mirrors schema-service's `ClosureItemType`/`ClosureMember`
 * (`approachDependencyClosure.ts`, EAINT-844 Phase 1) — the wire contract of
 * `GET /approaches/:systemName/closure`. Declared locally rather than imported: the backend
 * type lives in a schema-service-only module, and this lint is pure/no-I/O, taking whatever the
 * caller already fetched.
 */
export type ClosureItemType = 'arc_template' | 'context' | 'orbit_template' | 'metaspace' | 'schema' | 'composition';

export interface ClosureMember {
  itemType: ClosureItemType;
  systemName: string;
  version?: number;
}

export interface ApproachClosure {
  members: ClosureMember[];
}

export interface ClosureLintFinding {
  componentId: string;
  itemType: ClosureItemType;
  systemName: string;
  reason: 'out_of_closure';
}

function isCodeBackedType(type: string): boolean {
  return hasAdapter(type) || SPACE_CATALOG_TYPES.has(type);
}

function closureKey(itemType: ClosureItemType, systemName: string): string {
  return `${itemType}:${systemName}`;
}

/**
 * Walks a draft's composition/context refs against an approach's dependency closure (SC7):
 *
 * - Every `a2ui.components` entry whose `component` type is not code-backed (no adapter, not in
 *   the code-backed catalog) addresses a stored composition by `system_name` — same detection
 *   `useCompositionResolver` uses.
 * - Every entry carrying a `contextViewBinding.contextSystemName` (set by the composer's
 *   `setContextViewBinding` edit — see `composerStore.ts`) references a context.
 *
 * Pure and I/O-free: the caller fetches the closure (`GET /approaches/:systemName/closure`) and
 * passes it in. Returns one finding per out-of-closure ref, chip data for the composer UI.
 */
export function lintClosure(draft: SpaceDocument, closure: ApproachClosure): ClosureLintFinding[] {
  const closed = new Set(closure.members.map((m) => closureKey(m.itemType, m.systemName)));
  const findings: ClosureLintFinding[] = [];

  draft.a2ui.components.forEach((entry) => {
    if (!isCodeBackedType(entry.component) && !closed.has(closureKey('composition', entry.component))) {
      findings.push({ componentId: entry.id, itemType: 'composition', systemName: entry.component, reason: 'out_of_closure' });
    }

    const binding = entry.contextViewBinding as ContextViewBinding | undefined;
    if (binding?.contextSystemName && !closed.has(closureKey('context', binding.contextSystemName))) {
      findings.push({
        componentId: entry.id,
        itemType: 'context',
        systemName: binding.contextSystemName,
        reason: 'out_of_closure',
      });
    }
  });

  return findings;
}

/**
 * Narrows an option list (the composer inspector's context/view dropdowns, SC7) to an
 * approach's closure. `closureContextSystemNames` undefined means org scope — no filtering, every
 * sphere-available option stays. Defined-but-not-containing means approach scope — only the
 * closure's own context members remain selectable, so the user can't bind a ref the lint would
 * immediately flag as out-of-closure.
 */
export function filterOptionsByClosureContexts<T extends { systemName: string }>(
  options: T[],
  closureContextSystemNames: string[] | undefined
): T[] {
  if (!closureContextSystemNames) return options;
  const allowed = new Set(closureContextSystemNames);
  return options.filter((option) => allowed.has(option.systemName));
}
